import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getOrder,
  getOrders,
} from "@redotech/redo-merchant-app-common/client/order";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Order } from "@redotech/redo-order-core-common/order";
import {
  ReturnFlow,
  Step,
} from "@redotech/redo-return-core-common/return-flow";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import SearchIcon from "@redotech/redo-web/icon-old/search.svg";
import { Text } from "@redotech/redo-web/text";
import { CanceledError } from "axios";
import { memo, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as claimReturnSharedCss from "./claim-return-shared.module.css";
import {
  OrderSearchResultCard,
  OrderSearchResultSkeleton,
} from "./order-search-result-card";

const returnTypeToCreateRoute: Record<ReturnTypeEnum, string> = {
  [ReturnTypeEnum.RETURN]: "returns/create-return",
  [ReturnTypeEnum.WARRANTY]: "warranties/create-warranty-claim",
  [ReturnTypeEnum.CLAIM]: "claims/create-claim",
  [ReturnTypeEnum.RECYCLE]: "recycle",
  [ReturnTypeEnum.WITHDRAWAL]: "withdrawals/create-withdrawal",
};

export interface MultipleChoiceQuestion {
  message: string;
  choices: string[];
}

// FIXME Similar, but not exactly the same as the MultipleChoiceAnswer type in redo/model/return
export interface MultipleChoiceAnswer {
  questionText: string;
  answer: string;
  reason: boolean;
}

export const parseFlow = (flow: ReturnFlow | undefined) => {
  if (!flow?.steps.length) {
    return { reasonOptions: [], multipleChoiceQuestions: [] };
  }
  const reasonOptions = new Set<string>();
  const multipleChoiceQuestions: MultipleChoiceQuestion[] = [];
  const visitedWithReason = new Set<string>();
  const getReturnReasons = (
    currentReason: string,
    step: any,
    pathStack: Set<string>,
  ) => {
    if (!step) {
      return;
    }

    if (pathStack.has(step._id)) {
      return;
    }

    const visitKey = `${step._id}:${currentReason}`;
    if (visitedWithReason.has(visitKey)) {
      return;
    }

    visitedWithReason.add(visitKey);
    const newPath = new Set([...pathStack, step._id]);

    switch (step.type) {
      case "submit_claim":
      case "submit_warranty":
      case "submit_repair":
      case "reject":
      case "return":
      case "block":
        if (currentReason) {
          reasonOptions.add(currentReason);
        }
        break;
      case "multiple_choice":
        for (const option of step.options ?? []) {
          if (!step.reason) {
            getReturnReasons(
              `${currentReason}`,
              flow.steps[option.next],
              newPath,
            );
          } else {
            reasonOptions.add(option.name);
            getReturnReasons(
              `${option.name}`,
              flow.steps[option.next],
              newPath,
            );
          }
        }
        break;
      case "condition":
        getReturnReasons(currentReason, flow.steps[step.nextTrue], newPath);
        getReturnReasons(currentReason, flow.steps[step.nextFalse], newPath);
        break;
      case "ab_test":
        for (const treatment of step.treatments) {
          getReturnReasons(currentReason, flow.steps[treatment.next], newPath);
        }
        break;
      default:
        getReturnReasons(currentReason, flow.steps[step.next], newPath);
        break;
    }
  };

  // Collect return reasons and multiple-choice questions.
  getReturnReasons("", flow.steps[0], new Set());
  for (const step of flow.steps) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const stepAsAny = step as any;
    if (stepAsAny.type === "multiple_choice" && !stepAsAny.reason) {
      const stepCasted = step as Step.MultipleChoice;
      const duplicateQuestion = multipleChoiceQuestions.find(
        (question: MultipleChoiceQuestion) =>
          question.message === stepCasted.message,
      );
      if (duplicateQuestion) {
        for (const option of stepCasted.options ?? []) {
          const index = multipleChoiceQuestions.findIndex(
            (question) => question.message === stepCasted.message,
          );
          if (!duplicateQuestion.choices.includes(option.name)) {
            const question = multipleChoiceQuestions[index];
            if (question) {
              question.choices.push(option.name);
            }
          }
        }
      } else {
        const question = {
          message: stepCasted.message,
          choices: stepCasted.options.map((option) => option.name),
        };
        multipleChoiceQuestions.push(question);
      }
    }
  }
  return { reasonOptions: Array.from(reasonOptions), multipleChoiceQuestions };
};

export const MerchantCreateReturnModal = memo(
  function MerchantCreateReturnModal({
    open,
    setOpen,
    returnType,
    relocationInfo,
  }: {
    open: boolean;
    setOpen: (open: boolean) => void;
    returnType: ReturnTypeEnum;
    relocationInfo?: {
      preselectedOrder: Order;
      currentView?: string;
      returnQueryParams?: { [key: string]: string };
    };
  }) {
    const { t } = useTranslation();
    const team = useContext(TeamContext);
    const [orderNumber, setOrderNumber] = useState<string>(
      relocationInfo?.preselectedOrder
        ? relocationInfo.preselectedOrder.shopify.name
        : "",
    );
    const navigate = useNavigate();
    const merchantClient = useRequiredContext(RedoMerchantClientContext);
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const [orderReturnability, setOrderReturnability] = useState<
      Record<string, boolean>
    >({});

    const [foundOrder, setFoundOrder] = useState<Order | null>(null);

    const [findOrdersLoad, findOrders] = useTriggerLoad(async (signal) => {
      if (team) {
        try {
          setFoundOrder(null);
          if (relocationInfo?.preselectedOrder?._id) {
            // We should be able to combine both of these endpoints when we move off of old merchant created returns
            const order = await getOrder(
              merchantClient,
              relocationInfo.preselectedOrder._id,
            );
            if (order) {
              const returnabilityResult =
                await merchantRpcClient.checkOrdersReturnability({
                  orderIds: [order._id],
                });
              setOrderReturnability(returnabilityResult.returnability);
            }
            return [order];
          } else {
            const isSearching = orderNumber.trim().length > 0;
            const { data } = await getOrders(merchantClient, {
              pageSize: isSearching ? 8 : 30,
              sort: isSearching
                ? undefined
                : { key: "date_created", direction: "desc" },
              signal,
              search: orderNumber,
            });

            if (!data.length) {
              return undefined;
            }

            const orderIds = data.map((order: Order) => order._id);
            const returnabilityResult =
              await merchantRpcClient.checkOrdersReturnability({ orderIds });
            setOrderReturnability(returnabilityResult.returnability);

            // When showing recent orders (not searching), filter to only show orders with returnable items
            if (!isSearching) {
              const returnableOrders = data.filter(
                (order) => returnabilityResult.returnability[order._id],
              );
              return returnableOrders.slice(0, 5);
            }

            return data;
          }
        } catch (e: any) {
          if (e instanceof CanceledError) {
            return undefined;
          }
          console.error("Error finding orders", e);
          throw new Error("Error finding orders");
        }
      }
      return undefined;
    });

    useEffect(() => {
      if (orderNumber || open) {
        void findOrders();
      }
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderNumber, open]);

    function renderOrderSelection(): JSX.Element | null {
      if (findOrdersLoad.pending) {
        return (
          <Flex dir="column" gap="md" mt="md">
            <OrderSearchResultSkeleton />
            <OrderSearchResultSkeleton />
          </Flex>
        );
      }

      if (findOrdersLoad.value && findOrdersLoad.value.length > 0) {
        const isSearching = orderNumber.trim().length > 0;
        const labelText = isSearching
          ? t("{{count}} results", { count: findOrdersLoad.value.length })
          : t("Recent orders");

        return (
          <Flex dir="column" gap="md" mt="lg">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {labelText}
            </Text>
            {findOrdersLoad.value.map((order: Order) => (
              <OrderSearchResultCard
                hasReturnableItems={orderReturnability[order._id]}
                key={order._id}
                onClick={() => {
                  amplitude.logEvent("create-merchantReturn", {
                    shopifyOrderId: order.shopify_id,
                  });
                  if (
                    relocationInfo?.returnQueryParams &&
                    relocationInfo.currentView
                  ) {
                    navigate(
                      `/stores/${team?._id}/${returnTypeToCreateRoute[returnType]}/${order._id}?${new URLSearchParams(
                        relocationInfo.returnQueryParams,
                      ).toString()}&supportView=${relocationInfo.currentView}`,
                    );
                  } else {
                    navigate(
                      `/stores/${team?._id}/order-management/${returnTypeToCreateRoute[returnType]}/${order._id}`,
                    );
                  }
                }}
                order={order}
                returnType={returnType}
              />
            ))}
          </Flex>
        );
      }
      return null;
    }

    const handleClose = () => {
      setOpen(false);
      if (!relocationInfo?.preselectedOrder) {
        setOrderNumber("");
        setFoundOrder(null);
      }
    };

    const modalTitle = (() => {
      switch (returnType) {
        case ReturnTypeEnum.RETURN:
          return t("Start a return");
        case ReturnTypeEnum.CLAIM:
          return t("Start a claim");
        case ReturnTypeEnum.WARRANTY:
          return t("Start a warranty claim");
        case ReturnTypeEnum.RECYCLE:
          return t("Start a recycle");
        case ReturnTypeEnum.WITHDRAWAL:
          return t("Start a withdrawal");
      }
    })();

    const subtitle = (() => {
      switch (returnType) {
        case ReturnTypeEnum.RETURN:
          return t("Find an order to start a return.");
        case ReturnTypeEnum.CLAIM:
          return t("Find an order to start a claim.");
        case ReturnTypeEnum.WARRANTY:
          return t("Find an order to start a warranty claim.");
        case ReturnTypeEnum.RECYCLE:
          return t("Find an order to start a recycle.");
        case ReturnTypeEnum.WITHDRAWAL:
          return t("Find an order to start a withdrawal.");
      }
    })();

    return (
      <RedoModal
        isOpen={open}
        modalSize="sm"
        onModalCloseRequested={handleClose}
        subtitle={subtitle}
        title={modalTitle}
      >
        <div className={claimReturnSharedCss.modalContent}>
          {!(relocationInfo?.preselectedOrder && foundOrder) && (
            <>
              <Text
                fontSize="sm"
                fontWeight="medium"
                pb="sm"
                textColor="secondary"
              >
                {findOrdersLoad.pending && relocationInfo?.preselectedOrder
                  ? t("Searching for order")
                  : t("Find order")}
              </Text>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  findOrders();
                }}
              >
                <RedoTextInput
                  IconLeading={SearchIcon}
                  placeholder={t("Enter order number")}
                  setValue={(e) => setOrderNumber(e)}
                  size="md"
                  value={orderNumber}
                />
                {findOrdersLoad.error && (
                  <div className={claimReturnSharedCss.error}>
                    {typeof findOrdersLoad.error === "string"
                      ? findOrdersLoad.error
                      : t("Error finding order")}
                  </div>
                )}
              </form>
            </>
          )}
          {!foundOrder && renderOrderSelection()}
        </div>
      </RedoModal>
    );
  },
);
